/*
 * Copyright (c) 2024 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { useEffect, useState } from "react";
import { ViaToast } from "via-react-design-system";

import { LoginSteps } from "pages/LoginWeb/types";

import { PhoneNumber } from "pages/LoginWeb/components/PhoneNumber";
import { Challenge } from "pages/LoginWeb/components/Challenge";
import { ProblemsReceiveCodeDialog } from "pages/LoginWeb/components/ProblemsReceiveCodeDialog";

import { logAnalyticsEvent } from "common/analytics";
import AnalyticsEventName from "common/analytics/types/AnalyticsEventName";

import { useClasses } from "./styles";
import { useLogin } from "./hooks/useLogin";

import { ReactComponent as IconViacalLogoSmall } from "resources/IconViacalLogoSmall.svg";
import { ReactComponent as LogoViaCal } from "resources/LogoViaCal.svg";

export const LoginPage = () => {
    const [currentStep, setCurrentStep] = useState<LoginSteps>(LoginSteps.PhoneNumber);
    const [userPhoneNumber, setUserPhoneNumber] = useState<string>("");
    const [showProblemsReceivingCodeDialog, setShowProblemsReceivingCodeDialog] = useState(false);

    const {
        loading,
        error,
        onPhoneSubmit,
        onRequestSms,
        onSubmitSmsCode,
        onRequestCodeByPhone,
        onClearError,
    } = useLogin();
    const classes = useClasses();

    const handlePhoneSubmit = async (countryCode: string, phoneNumber: string) => {
        const userPhone = `+(${countryCode}) ${phoneNumber}`;
        logAnalyticsEvent({
            info: {
                buttonName: "get_code",
                buttonType: "button",
                isResend: "false",
                pageName: "enter_phone",
                phoneNumber: userPhone,
                section: "login",
                sendType: "sms",
            },
            name: AnalyticsEventName.ButtonTap,
        });

        setUserPhoneNumber(userPhone);
        await onPhoneSubmit(countryCode, phoneNumber);
        setCurrentStep(LoginSteps.Challenge);
    };

    const closeProblemsReceivingCodeDialog = () => {
        logAnalyticsEvent({
            info: {
                buttonName: "resend_code_cancel",
                buttonType: "button",
                flowSubsection: "login_retry",
                isResend: "true",
                pageName: "submit_code",
                phoneNumber: userPhoneNumber,
                section: "login",
            },
            name: AnalyticsEventName.ButtonTap,
        });
        handleCloseDialog();
    };

    const handleCloseDialog = () => {
        setShowProblemsReceivingCodeDialog(false);
    };

    const handleResendCode = () => {
        onRequestSms();
        handleCloseDialog();

        logAnalyticsEvent({
            info: {
                buttonName: "resend_code_sms",
                buttonType: "button",
                flowSubsection: "login_retry",
                isResend: "true",
                pageName: "submit_code",
                phoneNumber: userPhoneNumber,
                section: "login",
                sendType: "sms",
            },
            name: AnalyticsEventName.ButtonTap,
        });
    };

    const handleGetPhoneCall = () => {
        onRequestCodeByPhone();
        handleCloseDialog();

        logAnalyticsEvent({
            info: {
                buttonName: "resend_code_phone",
                buttonType: "button",
                flowSubsection: "login_retry",
                isResend: "true",
                pageName: "submit_code",
                phoneNumber: userPhoneNumber,
                section: "login",
                sendType: "phoneCall",
            },
            name: AnalyticsEventName.ButtonTap,
        });
    };

    const handleChangePhoneNumber = () => {
        setCurrentStep(LoginSteps.PhoneNumber);
        handleCloseDialog();

        logAnalyticsEvent({
            info: {
                buttonName: "change_phone_number",
                buttonType: "button",
                flowSubsection: "login_retry",
                isResend: "true",
                pageName: "submit_code",
                phoneNumber: userPhoneNumber,
                section: "login",
            },
            name: AnalyticsEventName.ButtonTap,
        });
    };

    useEffect(() => {
        return () => {
            logAnalyticsEvent({
                info: {
                    errorMessage: "",
                    flowName: "login",
                    flowPlatform: "browser",
                    flowSubsection: "login_confirm",
                    isError: false,
                    payload: { phoneNumber: userPhoneNumber },
                },
                name: AnalyticsEventName.UserAuthentication,
            });
        };
    }, [userPhoneNumber]);

    return (
        <div className={classes.container}>
            <div className={classes.loginForm}>
                {currentStep === LoginSteps.PhoneNumber ? <LogoViaCal /> : <IconViacalLogoSmall />}
                <div className={classes.content}>
                    {currentStep === LoginSteps.PhoneNumber && (
                        <PhoneNumber onPhoneNumberSubmit={handlePhoneSubmit} isLoading={loading} />
                    )}
                    {currentStep === LoginSteps.Challenge && (
                        <Challenge
                            userPhoneNumber={userPhoneNumber}
                            onSubmit={onSubmitSmsCode}
                            onProblemsReceivingCodeClick={() =>
                                setShowProblemsReceivingCodeDialog(true)
                            }
                            loading={loading}
                            isError={!!error?.message}
                        />
                    )}
                </div>
            </div>
            <div className={classes.backgroundImg} />
            <ViaToast onClose={onClearError} {...error} showClearButton>
                {error?.message}
            </ViaToast>
            {showProblemsReceivingCodeDialog && (
                <ProblemsReceiveCodeDialog
                    onClose={closeProblemsReceivingCodeDialog}
                    onChangePhoneNumber={handleChangePhoneNumber}
                    onGetPhoneCall={handleGetPhoneCall}
                    onResend={handleResendCode}
                />
            )}
        </div>
    );
};
